import { getFileExtension, isDataUri, isNotNullOrEmpty, isString } from "../string";
import { fromDataUri } from "../arraybuffer";
import { ERR_FAILED, newError } from "../error";
import { fetchContentType } from "../async";
const SUPPORTED_MIMETYPES = new Array("image/png", "image/jpeg", "image/svg+xml", "image/gif");
const EXTENSION_MIMETYPE_MAP = new Map([
    [".png", "image/png"],
    [".jpg", "image/jpeg"],
    [".jpeg", "image/jpeg"],
    [".svg", "image/svg+xml"],
    [".gif", "image/gif"]
]);
/**
 * Loads the given image and returns it as an HTMLImageElement.
 */
export function loadImage(pImageSrc) {
    const tPromise = new Promise((resolve, reject) => {
        let tUrl = pImageSrc;
        let tRevoke = false;
        if (!isString(tUrl)) {
            tUrl = URL.createObjectURL(pImageSrc); //TODO: remove this cast after TS update (> 4.5.2)
            tRevoke = true;
        }
        const tImage = new Image();
        tImage.onload = () => {
            if (tRevoke) {
                URL.revokeObjectURL(tUrl);
            }
            resolve(tImage);
        };
        tImage.onerror = () => {
            if (tRevoke) {
                URL.revokeObjectURL(tUrl);
            }
            reject(newError(ERR_FAILED, "Failed to load image: " + pImageSrc));
        };
        tImage.src = tUrl;
    });
    return (tPromise);
}
/**
 * Returns the mime type of the given image source. Null will be returned if the mime type is
 * unknown or if its an unsupported type.
 */
export async function getImageMimeType(pImageSrc) {
    let tType;
    if (isString(pImageSrc)) {
        if (isDataUri(pImageSrc)) {
            //handle data uri
            for (const tMimeType of SUPPORTED_MIMETYPES) {
                if (pImageSrc.startsWith("data:" + tMimeType)) {
                    return (tMimeType);
                }
            }
        }
        else {
            //handle url
            const tExtension = getFileExtension(pImageSrc);
            if (tExtension === "") {
                tType = await fetchContentType(pImageSrc);
            }
            else {
                tType = EXTENSION_MIMETYPE_MAP.get(tExtension);
            }
        }
    }
    else {
        if (isNotNullOrEmpty(pImageSrc.type)) {
            //handle file or blob
            tType = pImageSrc.type;
        }
        else if (pImageSrc.name != null) {
            //handle file
            const tExtension = getFileExtension(pImageSrc.name);
            tType = EXTENSION_MIMETYPE_MAP.get(tExtension);
        }
    }
    //validate mime type
    if (isNotNullOrEmpty(tType)) {
        if (SUPPORTED_MIMETYPES.includes(tType)) {
            return tType;
        }
    }
    return (null);
}
/** Resizes the given image according to the resize options and returns it as a canvas. */
export function resizeToCanvas(pImage, pOptions) {
    const tCanvas = document.createElement("canvas");
    const tContext = tCanvas.getContext("2d");
    if (tContext == null)
        throw newError(ERR_FAILED, "Failed to get 2d context.");
    let tWidth = pOptions.width;
    let tHeight = pOptions.height;
    if (pOptions.keepAspectRatio !== false) {
        const tSrcAspect = pImage.width / pImage.height;
        if (tHeight * tSrcAspect > tWidth) {
            tHeight = tWidth / tSrcAspect;
        }
        else {
            tWidth = tHeight * tSrcAspect;
        }
    }
    tCanvas.width = tWidth;
    tCanvas.height = tHeight;
    tContext.drawImage(pImage, 0, 0, tCanvas.width, tCanvas.height);
    return (tCanvas);
}
/** Resizes the given image according to the resize options and returns it as a dataUri. */
export function resizeToDataUri(pImage, pOptions) {
    return (resizeToCanvas(pImage, pOptions).toDataURL(pOptions.mimeType));
}
/** Resizes the given image according to the resize options and returns it as a blob. */
export function resizeToBlob(pImage, pOptions) {
    return (canvasToBlob(resizeToCanvas(pImage, pOptions), pOptions.mimeType));
}
/**
 * Returns the image content of the given canvas as a blob.
 * If pMimeType is undefined it will be returned as a png file.
 */
export function canvasToBlob(pCanvas, pMimeType) {
    const tMimeType = pMimeType !== null && pMimeType !== void 0 ? pMimeType : "image/png";
    if (pCanvas.toBlob != null) {
        const tPromise = new Promise((resolve, reject) => {
            try {
                pCanvas.toBlob((pResult) => {
                    if (pResult != null)
                        resolve(pResult);
                    else
                        reject(newError(ERR_FAILED, "toBlob failed"));
                }, tMimeType);
            }
            catch (pError) {
                reject(pError);
            }
        });
        return (tPromise);
    }
    else {
        const tDataUri = pCanvas.toDataURL(tMimeType);
        const tBuffer = fromDataUri(tDataUri);
        return (Promise.resolve(new Blob([tBuffer], { type: tMimeType })));
    }
}
